// Layer 4 (UI) — the NSR & prices tab, per the locked comp (variant A, picked
// 25 Sep): revenue by concentrate, with each price set over its prior-week
// price on the same schedule, beside the recovery levers. The three NSR cards
// and the separate metal-price table fold into these two cards: their figures
// are this table's rows and the band's Net revenue cell. Presentational only.

import { Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { vizToneColor } from '@/versions/v1/modules/_viz/viz-tone';
import { CARD_TABLE, DowntimeCard, SYN_CHIP } from '@/versions/v1/modules/downtime/DowntimeCard';
import { PER_PPT_DAY_HEADING, recoveryLeverPanel, revenueByConcentrate } from './NsrTab.transform';
import type { CostPeriod } from './Scene.transform';

/** The opportunity that prices the Zn penalty away (IO-01, SMBS batch-age
 *  discipline): its chip opens that row in the register. */
const PENALTY_OPPORTUNITY = 'IO-01';

function RevenueByConcentrate({
  period,
  onOpenOpportunity,
}: {
  period: CostPeriod;
  onOpenOpportunity: (id: string) => void;
}) {
  const rows = revenueByConcentrate(period);
  return (
    <Commentable name="MetalPrices">
      <div className="min-w-0 min-[1440px]:col-span-8">
        <DowntimeCard title="Revenue by concentrate" actions={SYN_CHIP}>
          <div className={CARD_TABLE}>
            <Table wrap={false} className="tabular-nums" aria-label="Revenue by concentrate">
              <THead>
                <TR>
                  <TH>Concentrate</TH>
                  <TH className="text-right">Conc.</TH>
                  <TH className="text-right">Grade</TH>
                  <TH className="text-right">Payable</TH>
                  <TH className="text-right">Price</TH>
                  <TH className="text-right">TC/RC</TH>
                  <TH className="text-right">Penalty</TH>
                  <TH className="text-right">Net</TH>
                </TR>
              </THead>
              <TBody>
                {rows.map((row) => (
                  <TR key={row.product}>
                    <TD className="whitespace-nowrap font-semibold text-ink-primary">{row.product}</TD>
                    <TD className="whitespace-nowrap text-right">{row.concentrateTonnes}</TD>
                    <TD className="whitespace-nowrap text-right">{row.grade}</TD>
                    <TD className="whitespace-nowrap text-right">{row.payableMetal}</TD>
                    {/* K18: the US$ glyph added a character to each price; the right-aligned price column
                        drops its left pad so the table stays inside its 1470 px panel. */}
                    <TD className="whitespace-nowrap !pl-0 text-right" data-price-cell>
                      <span className="block text-ink-primary">{row.price}</span>
                      <span className="block text-xs text-muted">{row.priorWeek}</span>
                    </TD>
                    <TD className="whitespace-nowrap text-right">{row.tcrc}</TD>
                    <TD className="whitespace-nowrap text-right">
                      {row.penaltyRaw > 0 ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="font-semibold" style={{ color: vizToneColor('warn') }}>
                            {row.penalty}
                          </span>
                          <button
                            type="button"
                            onClick={() => onOpenOpportunity(PENALTY_OPPORTUNITY)}
                            className="rounded border border-[var(--hairline-strong)] px-1 font-mono text-[10px] text-ink-secondary transition-colors hover:bg-[var(--surface-1)] hover:text-ink-primary"
                            aria-label={`Open ${PENALTY_OPPORTUNITY} in the register`}
                          >
                            {PENALTY_OPPORTUNITY}
                          </button>
                        </span>
                      ) : (
                        <span className="text-muted">{row.penalty}</span>
                      )}
                    </TD>
                    <TD className="whitespace-nowrap text-right font-semibold text-ink-primary">{row.netRevenue}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </div>
          <p className="mt-2 text-xs text-muted">
            Net = payable × price − TC/RC − freight − penalty · prices from the smelter schedule
          </p>
        </DowntimeCard>
      </div>
    </Commentable>
  );
}

/** Per metal: the recovery gap, what it costs in the period, and the derived
 *  cost per point per day. Three figures that multiply out. */
function RecoveryLevers({ period }: { period: CostPeriod }) {
  const view = recoveryLeverPanel(period);
  return (
    <DowntimeCard title="Recovery levers" meta="gap to design" className="min-[1440px]:col-span-4">
      <div className={CARD_TABLE}>
        <Table wrap={false} className="tabular-nums" aria-label="Recovery levers">
          <THead>
            <TR>
              <TH>Metal</TH>
              <TH className="text-right">Gap</TH>
              <TH className="text-right">Period cost</TH>
              <TH className="text-right">{PER_PPT_DAY_HEADING}</TH>
            </TR>
          </THead>
          <TBody>
            {view.rows.map((row) => (
              <TR key={row.metal}>
                <TD className="font-semibold text-ink-primary">{row.metal}</TD>
                {/* A gap is recovery below the design point, a shortfall; a
                    positive reading means recovery is running ahead. */}
                <TD
                  className="whitespace-nowrap text-right font-semibold"
                  style={{ color: vizToneColor(row.meanGapPt < 0 ? 'warn' : 'ok') }}
                >
                  {row.gapPt}
                </TD>
                <TD className="whitespace-nowrap text-right text-ink-primary">{row.periodCost}</TD>
                <TD className="whitespace-nowrap text-right">{row.costPerPtPerDay}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
    </DowntimeCard>
  );
}

export function NsrTab({
  period,
  onOpenOpportunity,
}: {
  period: CostPeriod;
  onOpenOpportunity: (id: string) => void;
}) {
  return (
    // The concentrate table needs its eight columns' width: beside the levers
    // from 1440 px, stacked over them below that.
    <div className="grid min-w-0 gap-3 min-[1440px]:grid-cols-12 min-[1440px]:items-start">
      <RevenueByConcentrate period={period} onOpenOpportunity={onOpenOpportunity} />
      <RecoveryLevers period={period} />
    </div>
  );
}
